import type { CandidateList, CreatorList } from "@unbox/api";
import { Pencil01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useCandidateList, useEditCandidateList } from "@/api/candidate-lists";
import { useCreatorList, useEditCreatorList } from "@/api/creator-lists";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { FormField, FormItem } from "@/components/ui/form";
import { error, success } from "@/providers/ToastProvider";

type FormValues = {
  name: string;
  description: string;
};
type Props = { id: string; type?: "creator" | "candidate" };

const Inner = ({ id, list, type = "creator" }: Props & { list: CreatorList | CandidateList }) => {
  const [open, setOpen] = useState(false);
  const form = useForm<FormValues>({
    defaultValues: {
      name: list.name,
      description: list.description ?? "",
    },
  });
  const { mutateAsync: mutateCreator } = useEditCreatorList();
  const { mutateAsync: mutateCandidate } = useEditCandidateList();
  const mutateAsync = type === "candidate" ? mutateCandidate : mutateCreator;

  useEffect(
    () =>
      form.reset({
        name: list.name,
        description: list.description ?? "",
      }),
    [open, id],
  );

  const onSubmit: SubmitHandler<FormValues> = async (payload) => {
    console.log(payload);
    try {
      await mutateAsync({ ...payload, id });
      success("Creator list updated");

      setOpen(false);
    } catch (err) {
      error("Failed to update creator list");
      throw err;
    }
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button iconLeading={Pencil01} color="secondary">
        Edit List Details
      </Button>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="min-w-[40rem] relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
                <CloseButton
                  onClick={() => setOpen(false)}
                  theme="light"
                  size="lg"
                  className="absolute top-3 right-3"
                />
                <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6 pb-4">
                  <div className="z-10 flex flex-col gap-0.5">
                    <Heading slot="title" className="text-md font-semibold text-primary">
                      Edit List Details
                    </Heading>
                  </div>
                </div>
                <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          isRequired
                          maxLength={80}
                          label="Name"
                          placeholder="Creator List Name"
                          {...field}
                        ></Input>
                        <span className="text-xs">Max 80 characters</span>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <TextArea
                          label="Description"
                          maxLength={200}
                          placeholder="Creator List Description"
                          {...field}
                        ></TextArea>
                        <span className="text-xs">Max 200 characters</span>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 justify-end sm:flex-row sm:px-6 sm:pt-8 sm:pb-6 ">
                  <Button color="secondary" size="lg" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" size="lg" type="submit">
                    Save
                  </Button>
                </div>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

const CreatorListWrapper = (props: Props) => {
  const { data: list } = useCreatorList(props.id);
  if (!list)
    return (
      <Button isDisabled iconLeading={Pencil01} color="secondary">
        Edit List
      </Button>
    );
  return <Inner {...props} list={list} />;
};

const CandidateListWrapper = (props: Props) => {
  const { data: list } = useCandidateList(props.id);
  if (!list)
    return (
      <Button isDisabled iconLeading={Pencil01} color="secondary">
        Edit List
      </Button>
    );
  return <Inner {...props} list={list} />;
};

export const EditCreatorListModal = (props: Props) => {
  return props.type === "candidate" ? <CandidateListWrapper {...props} /> : <CreatorListWrapper {...props} />;
};
